import {Injectable} from "@angular/core";
import {RequestService} from "../../services/request/request.service";
import {KanbanBoardConfigurationModel, KanbanColum} from "./kanban-board-configuration.model";
import {KanbanBoardCards, KanbanBoardCardsModel} from "./kanban-board-cards.model";
import {KanbanBoardConfigurationResponseModel} from "./kanban-board-configuration-response.model";
import {SwimlaneSettingsModel} from "@syncfusion/ej2-angular-kanban";
import {InternationalizationService} from "../../services/internationalization/internationalization.service";
import {UserService} from "../../services/user/user.service";
import {KanbanBoardQueryModel} from "./kanban-board-query.model";
import {KanbanBoardCardUpdateRequestModel} from "./kanban-board-card-update-request.model";

@Injectable({
  providedIn: 'root'
})
export class KanbanBoardService {

  constructor(private requestService: RequestService, private i18nService: InternationalizationService, private userService: UserService) {}

  KANBAN_SAVED_QUERY_KEY_PREFIX: string = 'user.kanban.savedfilters'

  async getConfiguration(kanbanId: string) {
    let configResponse = await this.getControllerData("getConfiguration", {id: kanbanId});
    return configResponse ? this.mapConfig(configResponse as KanbanBoardConfigurationResponseModel, kanbanId) : null;
  }

  private mapConfig(configResponse: KanbanBoardConfigurationResponseModel, kanbanId: string) {
    let columns = configResponse.columns.map((c, index) => ({
      headerText: c.text,
      keyField: c.key,
      color: c.color,
      displayAddButton: index === 0,
      transitionColumns: configResponse.transitions
        .filter(t => t.fromStatusCode == c.key)
        .map(t => t.toStatusCode),
      allowDrag: c.disableDrag !== true,
    })) as KanbanColum[];

    const swimlaneSettings = configResponse.hasSwimlanes ? {
      keyField: 'swimlaneKey',
      textField: 'swimlaneLabel',
      showEmptyRow: true,
      allowDragAndDrop: false,
    } as SwimlaneSettingsModel : null;

    return {
      ...configResponse,
      id: kanbanId,
      columns: columns,
      swimlaneSettings: swimlaneSettings,
      queryOwnerKey: `${this.KANBAN_SAVED_QUERY_KEY_PREFIX}.${kanbanId}`
    } as KanbanBoardConfigurationModel
  }

  async getBoardData(kanbanConfig: KanbanBoardConfigurationModel, query: KanbanBoardQueryModel) {
    let boardData = await this.getControllerData("getData", {
      id: kanbanConfig.id,
      advancedFiltersQuery: query.advancedFiltersQuery,
      textSearch: query.textSearchValue,
      membersFilter: query.membersFilter}
    ) as KanbanBoardCards

    boardData.cards.forEach(c => {
      this.transformCard(kanbanConfig, c);
    });
    return boardData;
  }

  transformCard(kanbanConfig: KanbanBoardConfigurationModel, c: KanbanBoardCardsModel) {
    if (c.dueDate) {
      //TODO: Copied from table grid list-data.service, could be extracted in utils classes
      if (kanbanConfig.dueDateFieldType === 'date') {
        c.dueDateAsDate = this.i18nService.parseDate(c.dueDate, {format: 'yyyy-MM-dd'})
      } else {
        c.dueDateAsDate = new Date(c.dueDate)
      }
    }
  }

  async updateCard(kanbanId: string, cardUpdate: KanbanBoardCardUpdateRequestModel, transitionCode?: string | null) {
    return await this.getControllerData("updateCard", {
      kanbanId: kanbanId,
      cardUpdate: cardUpdate,
      transitionCode: transitionCode
    })
  }

  async addMember(kanbanId: string, newMemberUserId: string) {
    const configResponse = await this.getControllerData("updateConfiguration", {
      kanbanId: kanbanId,
      configUpdate: {
        newMember: newMemberUserId
      }
    }) as KanbanBoardConfigurationResponseModel

    return this.mapConfig(configResponse, kanbanId);
  }

  async deleteMember(kanbanId: string, userMemberIdToDelete: string) {
    const configResponse = await this.getControllerData("updateConfiguration", {
      kanbanId: kanbanId,
      configUpdate: {
        memberToDelete: userMemberIdToDelete
      }
    }) as KanbanBoardConfigurationResponseModel

    return this.mapConfig(configResponse, kanbanId);
  }

  private async getControllerData(method: string, data: any = {}) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Kanban")
      .method(method)
      .data(data)
      .execute();

    return response.data;
  }
}
